import { Box, Link as MuiLink, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

type BlogMarkdownProps = {
  content: string;
};

export const BlogMarkdown = ({ content }: BlogMarkdownProps) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        '& p': {
          typography: 'body1',
          color: 'text.secondary',
          lineHeight: 1.85,
          mb: 2,
        },
        '& h2': {
          typography: 'h5',
          fontWeight: 700,
          mt: 4,
          mb: 1.5,
          color: 'text.primary',
        },
        '& h3': {
          typography: 'h6',
          fontWeight: 700,
          mt: 3,
          mb: 1,
          color: 'text.primary',
        },
        '& ul, & ol': { pl: 2.5, mb: 2, color: 'text.secondary' },
        '& li': { mb: 0.75, lineHeight: 1.75 },
        '& a': { color: 'primary.main', fontWeight: 600 },
        '& strong': { color: 'text.primary', fontWeight: 700 },
        '& blockquote': {
          borderLeft: `4px solid ${theme.palette.mainColors.blue}`,
          bgcolor: 'action.hover',
          borderRadius: 1,
          px: 2.5,
          py: 1.5,
          my: 3,
          color: 'text.secondary',
          '& p:last-child': { mb: 0 },
        },
        '& code': {
          bgcolor: 'action.hover',
          px: 0.5,
          borderRadius: 0.5,
          fontSize: '0.9em',
        },
        '& img': {
          display: 'block',
          width: '100%',
          height: 'auto',
          borderRadius: 2,
          my: 3,
          boxShadow: `0 12px 40px ${alpha(
            theme.palette.mainColors.primary,
            0.12
          )}`,
        },
        '& hr': {
          border: 'none',
          borderTop: `1px solid ${theme.palette.greyShades.border2}`,
          my: 4,
        },
        '& table': {
          width: '100%',
          borderCollapse: 'collapse',
          my: 3,
          fontSize: '0.9rem',
        },
        '& th, & td': {
          border: `1px solid ${theme.palette.greyShades.border2}`,
          px: 2,
          py: 1.25,
          textAlign: 'left',
        },
        '& th': {
          bgcolor: 'action.hover',
          fontWeight: 700,
          color: 'text.primary',
        },
        '& td': { color: 'text.secondary' },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          // The page header is the post's only <h1>; drop the Markdown title.
          h1: () => null,
          a: ({ href, children }) => (
            <MuiLink
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              {children}
            </MuiLink>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};
